/* eslint-disable no-use-before-define */
import { Heading, HorizontalRule, PlaybackList } from '@piggybank/core';
import {
  Form,
  FormFeedback,
  Navigation,
  ReviewNavigate,
} from '@piggybank/form';
import entries from 'lodash/entries';
import every from 'lodash/every';
import get from 'lodash/get';
import isArray from 'lodash/isArray';
import isPlainObject from 'lodash/isPlainObject';
import keys from 'lodash/keys';
import omit from 'lodash/omit';
import values from 'lodash/values';
import T from 'prop-types';
import React, { Fragment, useMemo } from 'react';
import * as builtinCustomComponents from '../../CustomComponents';
import DynamicFormReadOnlyField from '../../DynamicFormReadOnlyField';
import {
  DF_KEYWORD_CUSTOM_FIELD,
  DF_KEYWORD_CUSTOM_INPUT,
} from '../../constants';
import {
  Optional,
  Translation,
  TranslationScope,
  Translations,
  useCustomComponentsContext,
  usePagesConfigContext,
  useReviewContext,
} from '../../context';
import { NavigationContextProvider } from '../../context';
import { getInitialFormValues } from '../../operators';
import {
  getSchemaAtCursor,
  isReadOnlyValue,
  pruneUnwantedPropertiesForPage,
} from '../../utils';
import { createFormValidator } from '../../validation';
import styles from './style.css';

export const navigateToPageWithIndex = (
  navigateTo,
  setInReviewEdit,
  formValues,
  pageIndex
) => {
  setInReviewEdit(true);

  return navigateTo(pageIndex, formValues);
};

export const navigateBack = (onPrevious, setInReviewEdit, formValues) => {
  setInReviewEdit(false);

  return onPrevious(formValues);
};

export const sortFormData = (sortOrder, formData) => {
  if (typeof sortOrder !== 'undefined') {
    return sortOrder.reduce((acc, propertyName) => {
      acc[propertyName] = formData[propertyName];
      return acc;
    }, {});
  }

  return formData;
};

export const filterFormData = (formData, cb) =>
  entries(formData)
    .filter(cb)
    .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});

export const renderDependantFields = (
  sortedFormData,
  propertyNames,
  getComponentProps,
  keyPath
) =>
  entries(
    filterFormData(sortedFormData, ([key]) => !propertyNames.includes(key))
  ).reduce(
    (acc, [key, value]) => [
      ...acc,
      resolveField(
        getComponentProps({
          id: key,
          keyPath: [...keyPath, key],
          formData: value,
        })
      ),
    ],
    []
  );

export const getCustomReviewFormatter = (
  metaData = {},
  customComponents = {}
) => {
  const customComponent =
    customComponents[metaData.componentName] ||
    builtinCustomComponents[metaData.componentName];

  return get(customComponent, 'reviewFormatter', null);
};

const resolveField = ({
  customComponents,
  formData,
  formValues,
  getMetadataForFieldName,
  getSortOrderForFieldNames,
  id,
  keyPath,
  validate,
  ...rest
}) => {
  const getComponentProps = props => ({
    id,
    formValues,
    getMetadataForFieldName,
    getSortOrderForFieldNames,
    validate,
    customComponents,
    ...props,
    ...rest,
  });

  if (isArray(formData) && formData.every(item => isPlainObject(item))) {
    return formData.map((item, index) =>
      resolveField(
        getComponentProps({
          keyPath: [...keyPath, index.toString()],
          formData: item,
        })
      )
    );
  }

  const readOnly = isReadOnlyValue(formValues?.readOnly, keyPath.join('.'));

  if (isPlainObject(formData)) {
    const concatKeyPath = keyPath.join('.');
    const metaData = getMetadataForFieldName(concatKeyPath);
    const sortOrder = getSortOrderForFieldNames(concatKeyPath);
    const sortedFormData = sortFormData(sortOrder, formData);

    if (typeof metaData !== 'undefined') {
      const schemaNode = getSchemaAtCursor(
        metaData.context.schemaPath,
        validate.schema
      );

      const isCheckboxGroup =
        metaData.context.parent === 'DynamicFormGroup' && schemaNode.properties
          ? every(
              values(schemaNode.properties),
              ({ type }) => type === 'boolean'
            )
          : false;

      if (isCheckboxGroup) {
        const propertyNames = keys(schemaNode.properties);

        return [
          <DynamicFormReadOnlyField
            formData={filterFormData(sortedFormData, ([key]) =>
              propertyNames.includes(key)
            )}
            formValues={formValues}
            formatter={getCustomReviewFormatter(metaData, customComponents)}
            key={id}
            metaData={{
              ...metaData,
              isCheckboxGroup,
            }}
            readOnly={readOnly}
            reviewPage
            validate={validate}
          />,
          ...renderDependantFields(
            sortedFormData,
            propertyNames,
            getComponentProps,
            keyPath
          ),
        ];
      }

      if (
        schemaNode[DF_KEYWORD_CUSTOM_FIELD] ||
        schemaNode[DF_KEYWORD_CUSTOM_INPUT]
      ) {
        return (
          <DynamicFormReadOnlyField
            formData={sortedFormData}
            formValues={formValues}
            formatter={getCustomReviewFormatter(metaData, customComponents)}
            key={id}
            metaData={{
              ...metaData,
              isCheckboxGroup,
            }}
            readOnly={readOnly}
            reviewPage
            validate={validate}
          />
        );
      }
    }

    return entries(sortedFormData).map(([key, value]) =>
      resolveField(
        getComponentProps({
          id: key,
          keyPath: [...keyPath, key],
          formData: value,
        })
      )
    );
  }

  const metaData = getMetadataForFieldName(keyPath.join('.'));

  return metaData ? (
    <DynamicFormReadOnlyField
      formData={formData}
      formValues={formValues}
      formatter={getCustomReviewFormatter(metaData, customComponents)}
      key={id}
      metaData={metaData}
      readOnly={readOnly}
      reviewPage
      validate={validate}
    />
  ) : null;
};

resolveField.propTypes = {
  id: T.string,
  validate: T.object,
  formData: T.object,
  keyPath: T.arrayOf(T.string),
  getMetadataForFieldName: T.func,
  getSortOrderForFieldNames: T.func,
  customComponents: T.object,
};

export const sortPages = (pageOrderKeys, pages) =>
  pageOrderKeys.reduce((sorted, orderKey) => {
    for (const page of pages) {
      if (page[0] === orderKey) {
        sorted.push(page);
        break;
      }
    }

    return sorted;
  }, []);

const ReviewDetails = ({
  context,
  formName,
  getFormStructure,
  getMetadataForFieldName,
  getSectionsForPageName,
  getSortOrderForFieldNames,
  navigateTo,
  renderChildren,
  setPageLevelErrors,
  validate,
  ...rest
}) => {
  const { readOnlyReviewSections, setInReviewEdit } = useReviewContext();
  const customComponents = useCustomComponentsContext();
  const { excludePages } = usePagesConfigContext();
  const { pageName } = context.keyPath;

  const pageIndexes = useMemo(
    () =>
      keys(get(validate, 'schema.properties', {}))
        .filter(pageKey => !excludePages.includes(pageKey))
        .reduce((acc, pageKey, index) => {
          acc[pageKey] = index;
          return acc;
        }, {}),
    []
  );

  return (
    <Translations>
      {({ translations }) => {
        const pageProperties = get(validate.schema.properties, pageName, {});
        const pagePropertiesExist = values(pageProperties).length > 0;

        const initialValues = {
          [pageName]: getInitialFormValues(pageProperties),
        };

        const getErrors = createFormValidator({
          validate,
          pageName,
          translations,
          getMetadataForFieldName,
          getFormStructure,
          getSectionsForPageName,
          getSortOrderForFieldNames,
        });

        return (
          <Form
            className={styles.reviewDetails}
            initialValues={initialValues}
            name={formName}
            onSubmit={(contextOnSubmit, { values: formValues }) => {
              const prunedFormValues = pruneUnwantedPropertiesForPage({
                pageName,
                formValues,
                getFormStructure,
              });

              return contextOnSubmit({
                values: prunedFormValues,
              });
            }}
            validate={values => {
              const { errors, messages } = getErrors(values);

              setPageLevelErrors(errors);

              return messages;
            }}
          >
            {({ values: formValues }) => (
              <>
                {global.__DEBUG__ ? (
                  <pre>
                    {JSON.stringify(
                      { formValues, errors: getErrors(formValues) },
                      null,
                      2
                    )}
                  </pre>
                ) : null}
                <TranslationScope path={pageName}>
                  <Heading level={1}>
                    <Translation path="heading" />
                  </Heading>
                  <Optional>
                    <Translation marginBottom={4} path="description" />
                  </Optional>
                </TranslationScope>
                <FormFeedback data-testid={`formfeedback-${pageName}`} />

                {sortPages(
                  Object.keys(validate.schema.properties),
                  entries(omit(formValues, pageName))
                ).map(([pageName, pageFormData]) => (
                  <TranslationScope key={pageName} path={pageName}>
                    <Optional>
                      <Translation path="heading">
                        {title => {
                          const content = (
                            <PlaybackList type="review">
                              {(
                                getSectionsForPageName(pageName) || [
                                  { items: keys(pageFormData) },
                                ]
                              ).map(({ items, sectionName }, index) => (
                                <Fragment key={`review-playback-item-${index}`}>
                                  <Optional>
                                    <Heading level={3}>
                                      <Translation
                                        path={`${pageName}.${sectionName}.heading`}
                                      />
                                    </Heading>
                                  </Optional>

                                  {items.map(inputName =>
                                    resolveField({
                                      id: inputName,
                                      keyPath: [pageName, inputName],
                                      formValues,
                                      formData: get(pageFormData, inputName),
                                      getMetadataForFieldName,
                                      getSortOrderForFieldNames,
                                      validate,
                                      customComponents,
                                      context,
                                      ...rest,
                                    })
                                  )}
                                </Fragment>
                              ))}
                            </PlaybackList>
                          );

                          return readOnlyReviewSections.includes(pageName) ? (
                            <>
                              <Heading level={2} marginBottom={3}>
                                {title}
                              </Heading>
                              <HorizontalRule marginBottom={4} />
                              {content}
                            </>
                          ) : (
                            <ReviewNavigate
                              data-testid={`reviewNavigate-${pageIndexes[pageName]}`}
                              onClick={() =>
                                navigateToPageWithIndex(
                                  navigateTo,
                                  setInReviewEdit,
                                  formValues,
                                  pageIndexes[pageName]
                                )
                              }
                              title={title}
                            >
                              {content}
                            </ReviewNavigate>
                          );
                        }}
                      </Translation>
                    </Optional>
                  </TranslationScope>
                ))}

                {pagePropertiesExist && (
                  <NavigationContextProvider
                    navigateTo={navigateToPageWithIndex.bind(
                      null,
                      navigateTo,
                      setInReviewEdit,
                      formValues
                    )}
                  >
                    {renderChildren({
                      context: {
                        parent: 'DynamicFormPage',
                        keyPath: { pageName },
                        schemaPath: ['properties', pageName],
                        inReview: true,
                      },
                      formValues,
                    })}
                  </NavigationContextProvider>
                )}

                <Navigation
                  onPrevious={onPrevious =>
                    navigateBack(onPrevious, setInReviewEdit, formValues)
                  }
                />
              </>
            )}
          </Form>
        );
      }}
    </Translations>
  );
};

ReviewDetails.propTypes = {
  validate: T.func,
  formData: T.object,
  formName: T.string,
  navigateTo: T.func,
  renderChildren: T.func,
  getFormStructure: T.func,
  getMetadataForFieldName: T.func,
  getSectionsForPageName: T.func,
  getSortOrderForFieldNames: T.func,
  setPageLevelErrors: T.func,
  context: T.object,
};

export { ReviewDetails };
export default ReviewDetails;
